import { getUploadConfig } from "@/js/upload/api";

// Upload configuration with env parsing and safe defaults.
const DEFAULT_CHUNKED_UPLOAD_MIN_SIZE = 20 * 1024 * 1024;
const DEFAULT_CHUNKED_UPLOAD_CHUNK_SIZE_MB = 8;

const DEFAULT_CHUNKED_UPLOAD_MAX_IN_FLIGHT = 4;
const DEFAULT_CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS = [6, 12, 25];
const DEFAULT_CHUNKED_UPLOAD_HYSTERESIS_UP_PCT = 0.2;
const DEFAULT_CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT = 0.3;
const DEFAULT_CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS = 2000;

const DEFAULT_UPLOAD_MAX_IN_FLIGHT_INSTANCES = 2;
const DEFAULT_UPLOAD_BATCH_THRESHOLD = 20;
const DEFAULT_BATCH_UPLOAD_MAX_FILES = 500;
const DEFAULT_BATCH_UPLOAD_MAX_TOTAL_SIZE = 500 * 1024 * 1024;
const DEFAULT_BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE = 20 * 1024 * 1024;

const parseThresholds = value => {
  if (!value) {
    return DEFAULT_CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS;
  }
  const parsed = value
    .split(",")
    .map(v => Number(v.trim()))
    .filter(v => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b);
  return parsed.length ? parsed : DEFAULT_CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS;
};

const parsePct = (value, fallback) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) {
    return fallback;
  }
  return Math.min(num, 0.95);
};

const parseNumberWithDefault = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

let CHUNKED_UPLOAD_MIN_SIZE = Number(
  process.env.CHUNKED_UPLOAD_MIN_SIZE ||
    DEFAULT_CHUNKED_UPLOAD_MIN_SIZE
);
if (!Number.isFinite(CHUNKED_UPLOAD_MIN_SIZE) || CHUNKED_UPLOAD_MIN_SIZE <= 0) {
  CHUNKED_UPLOAD_MIN_SIZE = DEFAULT_CHUNKED_UPLOAD_MIN_SIZE;
}

let CHUNKED_UPLOAD_MAX_IN_FLIGHT = Number(
  process.env.CHUNKED_UPLOAD_MAX_IN_FLIGHT ||
    DEFAULT_CHUNKED_UPLOAD_MAX_IN_FLIGHT
);
if (
  !Number.isFinite(CHUNKED_UPLOAD_MAX_IN_FLIGHT) ||
  CHUNKED_UPLOAD_MAX_IN_FLIGHT < 1
) {
  CHUNKED_UPLOAD_MAX_IN_FLIGHT = DEFAULT_CHUNKED_UPLOAD_MAX_IN_FLIGHT;
}

let CHUNKED_UPLOAD_CHUNK_SIZE_MB = parseNumberWithDefault(
  process.env.CHUNKED_UPLOAD_CHUNK_SIZE_MB,
  DEFAULT_CHUNKED_UPLOAD_CHUNK_SIZE_MB
);
if (
  !Number.isFinite(CHUNKED_UPLOAD_CHUNK_SIZE_MB) ||
  CHUNKED_UPLOAD_CHUNK_SIZE_MB <= 0
) {
  CHUNKED_UPLOAD_CHUNK_SIZE_MB = DEFAULT_CHUNKED_UPLOAD_CHUNK_SIZE_MB;
}

let CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS = parseThresholds(
  process.env.CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS_MBPS
);

let CHUNKED_UPLOAD_HYSTERESIS_UP_PCT = parsePct(
  process.env.CHUNKED_UPLOAD_HYSTERESIS_UP_PCT,
  DEFAULT_CHUNKED_UPLOAD_HYSTERESIS_UP_PCT
);
let CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT = parsePct(
  process.env.CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT,
  DEFAULT_CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT
);
let CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS = Math.max(
  0,
  parseNumberWithDefault(
    process.env.CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS,
    DEFAULT_CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS
  )
);

let UPLOAD_MAX_IN_FLIGHT_INSTANCES = Number(
  process.env.UPLOAD_MAX_IN_FLIGHT_INSTANCES ||
    DEFAULT_UPLOAD_MAX_IN_FLIGHT_INSTANCES
);
if (
  !Number.isFinite(UPLOAD_MAX_IN_FLIGHT_INSTANCES) ||
  UPLOAD_MAX_IN_FLIGHT_INSTANCES < 1
) {
  UPLOAD_MAX_IN_FLIGHT_INSTANCES = DEFAULT_UPLOAD_MAX_IN_FLIGHT_INSTANCES;
}

let UPLOAD_BATCH_THRESHOLD = Number(
  process.env.UPLOAD_BATCH_THRESHOLD ||
    DEFAULT_UPLOAD_BATCH_THRESHOLD
);
if (!Number.isFinite(UPLOAD_BATCH_THRESHOLD) || UPLOAD_BATCH_THRESHOLD < 1) {
  UPLOAD_BATCH_THRESHOLD = DEFAULT_UPLOAD_BATCH_THRESHOLD;
}

let BATCH_UPLOAD_MAX_FILES = Number(
  process.env.BATCH_UPLOAD_MAX_FILES ||
    DEFAULT_BATCH_UPLOAD_MAX_FILES
);
if (!Number.isFinite(BATCH_UPLOAD_MAX_FILES) || BATCH_UPLOAD_MAX_FILES < 1) {
  BATCH_UPLOAD_MAX_FILES = DEFAULT_BATCH_UPLOAD_MAX_FILES;
}

let BATCH_UPLOAD_MAX_TOTAL_SIZE = Number(
  process.env.BATCH_UPLOAD_MAX_TOTAL_SIZE ||
    DEFAULT_BATCH_UPLOAD_MAX_TOTAL_SIZE
);
if (
  !Number.isFinite(BATCH_UPLOAD_MAX_TOTAL_SIZE) ||
  BATCH_UPLOAD_MAX_TOTAL_SIZE < 1
) {
  BATCH_UPLOAD_MAX_TOTAL_SIZE = DEFAULT_BATCH_UPLOAD_MAX_TOTAL_SIZE;
}

let BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE = Number(
  process.env.BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE ||
    DEFAULT_BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE
);
if (
  !Number.isFinite(BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE) ||
  BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE < 1
) {
  BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE = DEFAULT_BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE;
}

let uploadConfigLoaded = false;
let uploadConfigPromise = null;

const applyUploadConfig = config => {
  if (!config || typeof config !== "object") {
    return;
  }
  const toNumber = value => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const chunkedMinSize = toNumber(config.chunked_min_size);
  if (chunkedMinSize && chunkedMinSize > 0) {
    CHUNKED_UPLOAD_MIN_SIZE = chunkedMinSize;
  }
  const chunkSizeMb = toNumber(config.chunked_chunk_size_mb);
  if (chunkSizeMb && chunkSizeMb > 0) {
    CHUNKED_UPLOAD_CHUNK_SIZE_MB = chunkSizeMb;
  }
  const chunkedMaxInFlight = toNumber(config.chunked_max_in_flight);
  if (chunkedMaxInFlight && chunkedMaxInFlight > 0) {
    CHUNKED_UPLOAD_MAX_IN_FLIGHT = chunkedMaxInFlight;
  }
  const uploadMaxInFlight = toNumber(config.upload_max_in_flight_instances);
  if (uploadMaxInFlight && uploadMaxInFlight > 0) {
    UPLOAD_MAX_IN_FLIGHT_INSTANCES = uploadMaxInFlight;
  }
  const batchThreshold = toNumber(config.upload_batch_threshold);
  if (batchThreshold && batchThreshold > 0) {
    UPLOAD_BATCH_THRESHOLD = batchThreshold;
  }
  const batchMaxFiles = toNumber(config.batch_max_files);
  if (batchMaxFiles && batchMaxFiles > 0) {
    BATCH_UPLOAD_MAX_FILES = batchMaxFiles;
  }
  const batchMaxTotal = toNumber(config.batch_max_total_size);
  if (batchMaxTotal && batchMaxTotal > 0) {
    BATCH_UPLOAD_MAX_TOTAL_SIZE = batchMaxTotal;
  }
  const batchMaxSingle = toNumber(config.batch_max_single_file_size);
  if (batchMaxSingle && batchMaxSingle > 0) {
    BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE = batchMaxSingle;
  }

  if (config.chunked_concurrency_thresholds_mbps) {
    if (Array.isArray(config.chunked_concurrency_thresholds_mbps)) {
      const parsed = config.chunked_concurrency_thresholds_mbps
        .map(value => Number(value))
        .filter(value => Number.isFinite(value) && value > 0)
        .sort((a, b) => a - b);
      if (parsed.length) {
        CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS = parsed;
      }
    } else {
      CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS = parseThresholds(
        String(config.chunked_concurrency_thresholds_mbps)
      );
    }
  }

  const hysteresisUp = toNumber(config.chunked_hysteresis_up_pct);
  if (hysteresisUp != null && hysteresisUp >= 0) {
    CHUNKED_UPLOAD_HYSTERESIS_UP_PCT = Math.min(hysteresisUp, 0.95);
  }
  const hysteresisDown = toNumber(config.chunked_hysteresis_down_pct);
  if (hysteresisDown != null && hysteresisDown >= 0) {
    CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT = Math.min(hysteresisDown, 0.95);
  }
  const cooldownMs = toNumber(config.chunked_concurrency_cooldown_ms);
  if (cooldownMs != null && cooldownMs >= 0) {
    CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS = cooldownMs;
  }
};

// Fetch runtime upload config once; fall back to env defaults if it fails.
const ensureUploadConfig = () => {
  if (uploadConfigLoaded) {
    return Promise.resolve(null);
  }
  if (uploadConfigPromise) {
    return uploadConfigPromise;
  }
  uploadConfigPromise = getUploadConfig()
    .then(config => {
      applyUploadConfig(config);
      uploadConfigLoaded = true;
      return config;
    })
    .catch(() => {
      uploadConfigLoaded = true;
      return null;
    });
  return uploadConfigPromise;
};

export {
  BATCH_UPLOAD_MAX_FILES,
  BATCH_UPLOAD_MAX_SINGLE_FILE_SIZE,
  BATCH_UPLOAD_MAX_TOTAL_SIZE,
  CHUNKED_UPLOAD_CHUNK_SIZE_MB,
  CHUNKED_UPLOAD_CONCURRENCY_COOLDOWN_MS,
  CHUNKED_UPLOAD_CONCURRENCY_THRESHOLDS,
  CHUNKED_UPLOAD_HYSTERESIS_DOWN_PCT,
  CHUNKED_UPLOAD_HYSTERESIS_UP_PCT,
  CHUNKED_UPLOAD_MAX_IN_FLIGHT,
  CHUNKED_UPLOAD_MIN_SIZE,
  UPLOAD_BATCH_THRESHOLD,
  UPLOAD_MAX_IN_FLIGHT_INSTANCES,
  applyUploadConfig,
  ensureUploadConfig
};
